import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Smartphone, 
  Menu, 
  X, 
  Bell, 
  Home,
  User, 
  Users, 
  Briefcase, 
  ChevronRight,
  ChevronLeft,
  AlertCircle,
  CheckCircle,
  Clock,
  Coffee,
  ShoppingBag,
  Utensils,
  TrendingUp,
  Settings,
  LogOut,
  ChevronDown,
  DollarSign,
  FileText,
  Edit3,
  ExternalLink,
  Plus,
  Search,
  Filter,
  MoreVertical,
  Folder,
  Archive,
  Trash2,
  Loader
} from 'lucide-react';
import Sidebar from '../main/Sidebar';
import Topbar from '../main/Topbar';
import { ApiProject, formatProjectNameWithType, getProjectList, resolveProjectPrimaryUrl } from '../../utils/project';
import { formatDateFromApi } from '../../utils/dateTime';
import {
  attachProjectProfileSettingsToSnapshot,
  fetchSelectedProjectProfileSettings,
  getProjectFeatureLabel,
  PROJECT_FEATURE_OPTIONS,
  type SelectedProjectProfileSettings,
} from '../../utils/projectConfig';

const EMPTY_PROJECT_PROFILE_SETTINGS: SelectedProjectProfileSettings = {
  featureCodes: [],
};

// Types
interface Project {
  id: string;
  name: string;
  projectType?: string;
  defaultBrandCode?: string;
  earnMethod?: string;
  code: string;
  description: string;
  logo?: string;
  createdAt: string;
  updatedAt: string;
  /** PROJECT / FEATURE (ENUM_MULTI) */
  featureCodes: string[];
  status: 'ACTIVE' | 'INACTIVE' | 'PENDING' | 'ARCHIVED';
  monthlySpend: number;
  owner: string;
  team: number;
}

// API Response Types (이미 utils/project.ts로 이동)

const ProjectMain: React.FC = () => {
  const navigate = useNavigate();
  const [isDesktopSidebarOpen, setIsDesktopSidebarOpen] = useState<boolean>(true);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState<boolean>(false);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState<boolean>(false);
  const [activeMenu, setActiveMenu] = useState<string>('projects');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [selectedProjects, setSelectedProjects] = useState<string[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);

  // Fetch projects from API
  const fetchProjects = async () => {
    setIsLoading(true);
    setError('');
    
    try {
      const response = await getProjectList({
        keyword: searchTerm,
        status: filterStatus === 'all' ? '' : filterStatus,
        type: ''
      });
      
      if (response && response.projects) {
        const list = response.projects;
        const profileSettingsList = await Promise.all(
          list.map((apiProject) =>
            fetchSelectedProjectProfileSettings(apiProject.code).catch((err) => {
              console.warn('프로젝트 설정 조회 실패:', apiProject.code, err);
              return EMPTY_PROJECT_PROFILE_SETTINGS;
            })
          )
        );
        const logoUrlList = await Promise.all(
          list.map((apiProject) =>
            resolveProjectPrimaryUrl(
              apiProject.code,
              apiProject.primaryVersion,
              apiProject.updatedAt
            ).catch((err) => {
              console.warn('프로젝트 대표 파일 URL 조회 실패:', apiProject.code, err);
              return null;
            })
          )
        );
        const transformedProjects = list.map((apiProject: ApiProject, i: number) => ({
          id: apiProject.code,
          name: apiProject.name,
          projectType: apiProject.projectType,
          code: apiProject.code,
          description: apiProject.description,
          logo: logoUrlList[i] ?? undefined,
          createdAt: formatDateFromApi(apiProject.createdAt) || apiProject.createdAt,
          updatedAt: formatDateFromApi(apiProject.updatedAt) || apiProject.updatedAt,
          featureCodes: profileSettingsList[i]?.featureCodes ?? [],
          earnMethod: profileSettingsList[i]?.earnMethod,
          defaultBrandCode: profileSettingsList[i]?.defaultBrandCode,
          status: mapApiStatusToProjectStatus(apiProject.status),
          monthlySpend: Math.floor(Math.random() * 500000), // 랜덤 월 비용 (추가 설계 중)
          owner: apiProject.createdBy,
          team: Math.floor(Math.random() * 10) + 1, // 랜덤 팀원 수 (추가 설계 중)
        }));

        setProjects(transformedProjects);
      } else {
        setProjects([]);
      }
    } catch (error: any) {
      console.error('프로젝트 목록 조회 오류:', error);
      const serverMessage = error instanceof Error ? error.message : '';
      const defaultMessage = '프로젝트 목록을 불러오는데 실패했습니다.';
      const fullMessage = serverMessage ? `${defaultMessage}\n\n서버 메시지: ${serverMessage}` : defaultMessage;
      setError(fullMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Map API status to project status
  const mapApiStatusToProjectStatus = (apiStatus: string): Project['status'] => {
    switch (apiStatus.toLowerCase()) {
      case 'ACTIVE':
        return 'ACTIVE';
      case 'INACTIVE':
        return 'INACTIVE';
      case 'PENDING':
        return 'PENDING';
      case 'ARCHIVED':
        return 'ARCHIVED';
      default:
        return 'PENDING';
    }
  };

  // Load projects on component mount
  useEffect(() => {
    fetchProjects();
  }, []);

  // Close dropdown menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!(event.target as HTMLElement).closest('.dropdown-menu') && 
          !(event.target as HTMLElement).closest('button')) {
        document.querySelectorAll('.dropdown-menu').forEach(menu => {
          menu.classList.add('hidden');
        });
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const getFeatureIcon = (featureValue: string, className?: string): React.ReactElement => {
    const iconProps = { className: className || 'w-4 h-4' };
    switch (featureValue) {
      case 'SHOWCASE':
        return <Utensils {...iconProps} />;
      case 'MEMBERSHIP':
        return <Users {...iconProps} />;
      case 'MOBILE_ORDER':
        return <Coffee {...iconProps} />;
      case 'MALL':
        return <ShoppingBag {...iconProps} />;
      default:
        return <Briefcase {...iconProps} />;
    }
  };

  const getStatusBadge = (status: Project['status']): React.ReactElement => {
    switch (status) {
      case 'ACTIVE':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <span className="w-1.5 h-1.5 mr-1 bg-green-400 rounded-full"></span>
            활성
          </span>
        );
      case 'INACTIVE':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            <span className="w-1.5 h-1.5 mr-1 bg-gray-400 rounded-full"></span>
            비활성
          </span>
        );
      case 'PENDING':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            <span className="w-1.5 h-1.5 mr-1 bg-yellow-400 rounded-full"></span>
            대기중
          </span>
        );
      case 'ARCHIVED':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
            <span className="w-1.5 h-1.5 mr-1 bg-red-400 rounded-full"></span>
            보관됨
          </span>
        );
    }
  };

  const formatCurrency = (amount: number): string => {
    return `₩${amount.toLocaleString()}`;
  };

  // Filter projects based on search and filter
  const filteredProjects = projects.filter(project => {
    const matchesSearch = project.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          project.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          project.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterStatus === 'all' || project.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  const handleSelectAll = () => {
    if (selectedProjects.length === filteredProjects.length) {
      setSelectedProjects([]);
    } else {
      setSelectedProjects(filteredProjects.map(p => p.id));
    }
  };

  const handleSelectProject = (projectId: string) => {
    if (selectedProjects.includes(projectId)) {
      setSelectedProjects(selectedProjects.filter(id => id !== projectId));
    } else {
      setSelectedProjects([...selectedProjects, projectId]);
    }
  };

  return (
    <div className="h-screen bg-gray-50 flex overflow-hidden">
      <Sidebar
        isDesktopSidebarOpen={isDesktopSidebarOpen}
        setIsDesktopSidebarOpen={setIsDesktopSidebarOpen}
        isMobileSidebarOpen={isMobileSidebarOpen}
        setIsMobileSidebarOpen={setIsMobileSidebarOpen}
        isProfileDropdownOpen={isProfileDropdownOpen}
        setIsProfileDropdownOpen={setIsProfileDropdownOpen}
        activeMenu={activeMenu}
        setActiveMenu={setActiveMenu}
      />

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <Topbar
          title="프로젝트"
          subtitle="모든 프로젝트를 한눈에 확인하고 관리하세요"
          isMobileSidebarOpen={isMobileSidebarOpen}
          setIsMobileSidebarOpen={setIsMobileSidebarOpen}
          isProfileDropdownOpen={isProfileDropdownOpen}
          setIsProfileDropdownOpen={setIsProfileDropdownOpen}
        />

        {/* Projects Content */}
        <main className="flex-1 p-4 sm:p-6 overflow-y-auto">
          <div className="max-w-7xl mx-auto">
            {/* Page Header */}
            {/* <div className="mb-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">프로젝트 관리</h2>
              <p className="text-gray-600">모든 프로젝트를 한눈에 확인하고 관리하세요.</p>
            </div> */}

            {/* Actions Bar */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                {/* Search and Filter */}
                <div className="flex flex-col sm:flex-row gap-3 flex-1">
                  <div className="relative flex-1 max-w-md">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      placeholder="프로젝트 검색..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900"
                    />
                  </div>
                  <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900"
                  >
                    <option value="all">모든 상태</option>
                    <option value="ACTIVE">활성</option>
                    <option value="INACTIVE">비활성</option>
                    <option value="PENDING">대기중</option>
                    <option value="ARCHIVED">보관됨</option>
                  </select>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-2">
                <button 
                    onClick={() => navigate('/projects/create')}
                    className="px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors flex items-center"
                  >
                    <Plus className="w-5 h-5 mr-2" />
                    <span className="hidden sm:inline">새 프로젝트</span>
                  </button>
                </div>
              </div>

              {/* Selected Actions */}
              {selectedProjects.length > 0 && (
                <div className="mt-4 pt-4 border-t flex items-center justify-between">
                  <span className="text-sm text-gray-600">
                    {selectedProjects.length}개 선택됨
                  </span>
                  <div className="flex gap-2">
                    <button className="px-3 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">
                      <Archive className="w-4 h-4 inline mr-1" />
                      보관
                    </button>
                    <button className="px-3 py-1 text-sm border border-red-300 text-red-600 rounded-lg hover:bg-red-50">
                      <Trash2 className="w-4 h-4 inline mr-1" />
                      삭제
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Loading State */}
            {isLoading && (
              <div className="text-center py-12">
                <Loader className="w-8 h-8 text-gray-400 mx-auto mb-4 animate-spin" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">프로젝트 목록을 불러오는 중...</h3>
                <p className="text-gray-500">잠시만 기다려주세요.</p>
              </div>
            )}

            {/* Error State */}
            {error && !isLoading && (
              <div className="flex flex-col items-center justify-center py-12">
                <AlertCircle className="w-16 h-16 text-red-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">프로젝트 목록을 불러올 수 없습니다</h3>
                <p className="text-gray-500 mb-4 whitespace-pre-line text-center max-w-md mx-auto">{error}</p>
                <button 
                  onClick={fetchProjects}
                  className="px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors"
                >
                  다시 시도
                </button>
              </div>
            )}

            {/* Projects Grid */}
            {!isLoading && !error && (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                {filteredProjects.map((project) => (
                <div
                  key={project.id}
                  className="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow cursor-pointer"
                  onClick={async () => {
                    setSelectedProject(project);
                    localStorage.setItem('selectedProject', JSON.stringify(project));
                    try {
                      const withDefaults = await attachProjectProfileSettingsToSnapshot(project);
                      localStorage.setItem('selectedProject', JSON.stringify(withDefaults));
                      setSelectedProject(withDefaults);
                    } catch (err) {
                      console.warn('프로젝트 기본 설정 병합 실패:', err);
                    }
                    navigate(`/projects/${project.code}/dashboard`);
                  }}
                >
                  {/* Project Content */}
                  <div className="p-6">
                    {/* Logo and Basic Info */}
                    <div className="flex items-start space-x-4 mb-4">
                      {/* Logo - Larger Size */}
                      <div className="flex-shrink-0">
                        <div className="w-32 h-32 bg-gray-100 rounded-lg flex items-center justify-center">
                          {project.logo ? (
                            <img
                              src={project.logo}
                              alt={`${formatProjectNameWithType(project.name, project.projectType)} 로고`}
                              className="w-full h-full object-cover rounded-lg"
                              onError={(e) => {
                                // 로고 로드 실패 시 기본 아이콘 표시
                                const target = e.target as HTMLImageElement;
                                target.style.display = 'none';
                                const parent = target.parentElement;
                                if (parent) {
                                  parent.innerHTML = '<div class="w-full h-full flex items-center justify-center text-5xl">📁</div>';
                                }
                              }}
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-5xl">📁</div>
                          )}
                        </div>
                      </div>
                      
                      {/* Name, Code, Created Date, Status */}
                      <div className="flex-1 min-w-0">
                        <h3 className="text-lg font-semibold text-gray-900 truncate mb-1">
                          {formatProjectNameWithType(project.name, project.projectType)}
                        </h3>
                        <p className="text-sm text-gray-500 mb-2">{project.code}</p>
                        <p className="text-xs text-gray-400 mb-3">생성일: {project.createdAt}</p>
                        
                        {/* Status with Menu */}
                        <div className="flex items-center space-x-2">
                          {getStatusBadge(project.status)}
                          
                          {/* Dropdown Menu */}
                          <div className="relative">
                            <button 
                              onClick={(e) => {
                                e.stopPropagation();
                                const menuId = `menu-${project.id}`;
                                const currentMenu = document.getElementById(menuId);
                                // Close all other menus
                                document.querySelectorAll('.dropdown-menu').forEach(menu => {
                                  if (menu.id !== menuId) {
                                    menu.classList.add('hidden');
                                  }
                                });
                                // Toggle current menu
                                currentMenu?.classList.toggle('hidden');
                              }}
                              className="p-1 hover:bg-gray-100 rounded transition-colors"
                            >
                              <MoreVertical className="w-4 h-4 text-gray-600" />
                            </button>
                            
                            {/* Dropdown Content */}
                            <div 
                              id={`menu-${project.id}`}
                              className="dropdown-menu hidden absolute right-0 mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-10"
                            >
                              <button 
                                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/projects/${project.code}/update`);
                                }}
                              >
                                <Edit3 className="w-4 h-4 mr-2" />
                                수정
                              </button>
                              <div className="border-t border-gray-100 my-1"></div>
                              <div className="px-4 py-2 text-xs text-gray-500">상태 변경</div>
                              <button 
                                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  console.log('Change status to ACTIVE:', project.id);
                                }}
                              >
                                <span className="w-2 h-2 bg-green-500 rounded-full inline-block mr-2"></span>
                                활성으로 변경
                              </button>
                              <button 
                                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  console.log('Change status to INACTIVE:', project.id);
                                }}
                              >
                                <span className="w-2 h-2 bg-gray-400 rounded-full inline-block mr-2"></span>
                                비활성으로 변경
                              </button>
                              <button 
                                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  console.log('Change status to PENDING:', project.id);
                                }}
                              >
                                <span className="w-2 h-2 bg-yellow-400 rounded-full inline-block mr-2"></span>
                                대기중으로 변경
                              </button>
                              <button 
                                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  console.log('Archive project:', project.id);
                                }}
                              >
                                <span className="w-2 h-2 bg-red-400 rounded-full inline-block mr-2"></span>
                                보관함으로 이동
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Services */}
                    <div>
                      <p className="text-xs text-gray-500 mb-2">제공 서비스</p>
                      {project.featureCodes.length === 0 ? (
                        <p className="text-sm text-gray-500">프로젝트 설정에서 사용 기능을 선택해 주세요.</p>
                      ) : (
                        <div className="flex flex-wrap gap-2">
                          {project.featureCodes.map((code) => (
                            <div
                              key={code}
                              className="inline-flex items-center px-3 py-1.5 bg-gray-100 rounded-lg"
                              title={getProjectFeatureLabel(code)}
                            >
                              {getFeatureIcon(code, 'w-4 h-4 mr-1.5 text-gray-600')}
                              <span className="text-sm text-gray-700">{getProjectFeatureLabel(code)}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              </div>
            )}

            {/* Empty State */}
            {!isLoading && !error && filteredProjects.length === 0 && (
              <div className="text-center py-12">
                <Folder className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">프로젝트가 없습니다</h3>
                <p className="text-gray-500 mb-4">검색 조건에 맞는 프로젝트가 없습니다.</p>
                <button 
                  onClick={() => navigate('/projects/create')}
                  className="px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors"
                >
                  새 프로젝트 만들기
                </button>
              </div>
            )}

            {/* Summary Stats */}
            {/* {!isLoading && !error && (
              <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                  <div className="text-2xl font-bold text-gray-900">
                    {projects.length}
                  </div>
                  <p className="text-sm text-gray-600">전체 프로젝트</p>
                </div>
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                  <div className="text-2xl font-bold text-green-600">
                    {projects.filter(p => p.status === 'ACTIVE').length}
                  </div>
                  <p className="text-sm text-gray-600">활성 프로젝트</p>
                </div>
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                  <div className="text-2xl font-bold text-gray-900">
                    ₩{Math.floor(projects.reduce((sum, p) => sum + p.monthlySpend, 0) / 1000)}K
                  </div>
                  <p className="text-sm text-gray-600">월 총 비용</p>
                </div>
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                  <div className="text-2xl font-bold text-gray-900">
                    {projects.reduce((sum, p) => sum + p.team, 0)}
                  </div>
                  <p className="text-sm text-gray-600">전체 팀원 수</p>
                </div>
              </div>
            )} */}
          </div>
        </main>
      </div>
    </div>
  );
};

export default ProjectMain;